import { ModelConfig } from '@rematch/core';
import type { RootState } from '../models';
import {
  CordovaTapToPayCollectPaymentSuccessPayload,
  CordovaTapToPayDetectDeviceSuccessPayload,
  CordovaTapToPayErrorPayload,
} from '../../modules/CordovaPlugins.types';

import { getIOSVersion, getIsApp } from '../../modules/AppInfo';
import {
  getStripeConnectionToken,
  TapToPayConnectionStatus,
  TapToPayGeolocationAuthorizationStatus,
  TapToPayPlugin,
  TapToPayPluginErrors,
  TapToPayPluginEvent,
} from '../../modules/TapToPay';
import { ConnectionStatus, StripeTerminalState } from './StripeTerminal.model.types';
import { StripeTerminalConnectError } from './StripeTerminalConnectError';
import { StripeTerminalCollectPaymentError } from './StripeTerminalCollectPaymentError';
import { StripeTerminalGeolocationAuthorizationError } from './StripeTerminalGeolocationAuthorizationError';
import { StripeTerminalDisconnectError } from './StripeTerminalDisconnectError';
import { StripeTerminalProcessPaymentError } from './StripeTerminalProcessPaymentError';
import { formatError, log } from '../../modules/TapToPay/TapToPayLogger';

export const getDefaultState = (): StripeTerminalState => ({
  pluginLoaded: false,
  pluginInstance: null,
  connectionStatus: ConnectionStatus.NotConnected,
  softwareUpdateProgress: 0,
  shouldAutoReconnect: false,
  geolocationAuthorizationStatus: TapToPayGeolocationAuthorizationStatus.Unknown,
});

export const stripeTerminal: ModelConfig<StripeTerminalState> = {
  name: 'stripeTerminal',
  state: getDefaultState() as StripeTerminalState,
  selectors: slice => ({
    isPluginLoaded() {
      return slice(state => state.pluginLoaded);
    },
    isConnected() {
      return slice(state => state.connectionStatus === ConnectionStatus.Connected);
    },
    isConnecting() {
      return slice(state => state.connectionStatus === ConnectionStatus.InProgress);
    },
    isNotConnected() {
      return slice(state => state.connectionStatus === ConnectionStatus.NotConnected);
    },
    connectionProgress() {
      return slice(state => state.softwareUpdateProgress);
    },
    shouldAutoReconnect() {
      return slice(state => state.shouldAutoReconnect);
    },
    geolocationAuthorizationStatus() {
      return slice(state => state.geolocationAuthorizationStatus);
    },
  }),
  reducers: {
    onReaderConnected(state) {
      return ({
        ...state,
        connectionStatus: ConnectionStatus.Connected,
      });
    },
    onReaderConnecting(state) {
      return ({
        ...state,
        connectionStatus: ConnectionStatus.InProgress,
      });
    },
    onReaderNotConnected(state) {
      return ({
        ...state,
        connectionStatus: ConnectionStatus.NotConnected,
      });
    },
    onPluginLoaded(state, success: boolean) {
      return ({
        ...state,
        pluginLoaded: success,
      });
    },
    onPluginInstantiated(state, instance: TapToPayPlugin) {
      return ({
        ...state,
        pluginInstance: instance,
      });
    },
    onSoftwareUpdateProgressUpdated(state, softwareUpdateProgress: number) {
      return ({
        ...state,
        softwareUpdateProgress,
      });
    },
    onGeolocationAuthorizationStatusUpdated(state, status: TapToPayGeolocationAuthorizationStatus) {
      return ({
        ...state,
        geolocationAuthorizationStatus: status,
      });
    },
    onReset() {
      return getDefaultState();
    },
    setShouldAutoReconnect(state, shouldAutoReconnect: boolean) {
      return ({
        ...state,
        shouldAutoReconnect,
      });
    },
  },
  effects: () => ({
    async init() {
      const isIOSApp = getIsApp() && getIOSVersion();
      if (!isIOSApp) {
        this.onPluginLoaded(false);
        log('Not running in iOS App');
        return;
      }

      // It's in this format instead of async/await in a try/catch block because
      // we plan to keep and reuse one of these callbacks as the terminal delegate to deal with
      // events such as didDisconnectReader
      TapToPayPlugin.init(
        event => {
          this.onPluginLoaded(true);
          if (typeof event !== 'object') {
            // Initial load of plugin completed
            try {
              this.updateGeolocationAuthorizationStatus();
            } catch {
              log('Updating geolocation authorization status failed after plugin init');
            }
            return;
          }

          switch (event?.event) {
            case TapToPayPluginEvent.DidReportUnexpectedReaderDisconnect:
              log('didReportUnexpectedReaderDisconnect');
              this.onReaderNotConnected();
              break;
            case TapToPayPluginEvent.DidChangeLocationAuthorization:
              log('Geolocation Authorization Changed');
              this.updateGeolocationAuthorizationStatus();
              break;
            default:
              log(`Unexpected event for TapToPayPlugin.init() onSuccess: ${event}`);
          }
        },
        (payload?: CordovaTapToPayErrorPayload) => {
          log(`Failed to initialize Plugin: ${formatError(payload)}`);
          this.onPluginLoaded(false);
        },
      );
    },
    async connectReader(payload: {
      locationId: string,
      merchantName: string,
      stripeAcctId: string,
    }, state: RootState): Promise<void> {
      if (!state.stripeTerminal.pluginLoaded) {
        log('Connection aborted: Plugin not loaded');
        throw new StripeTerminalConnectError('Plugin not loaded');
      }
      if (state.stripeTerminal.connectionStatus === ConnectionStatus.Connected) {
        log('Connection skipped: Reader already connected');
        return;
      }
      const {
        locationId, merchantName, stripeAcctId,
      } = payload;

      const instance = new TapToPayPlugin({
        fetchToken: getStripeConnectionToken,
        locationId,
        merchantName,
        onBehalfOfId: stripeAcctId,
      });
      this.onPluginInstantiated(instance);
      this.onReaderConnecting();

      const handleSuccess = (
        resolve: (value: unknown) => void,
        event: CordovaTapToPayDetectDeviceSuccessPayload,
      ) => {
        if (typeof event !== 'object') return;
        log(JSON.stringify(event));
        switch (event.event) {
          case TapToPayPluginEvent.DidStartDiscoverReaders:
            break;
          case TapToPayPluginEvent.DidConnectReader:
            this.onReaderConnected();
            resolve(event);
            break;
          case TapToPayPluginEvent.DidStartInstallingUpdate:
            break;
          case TapToPayPluginEvent.DidReportReaderSoftwareUpdateProgress:
            this.onSoftwareUpdateProgressUpdated(event.payload);
            break;
          case TapToPayPluginEvent.DidFinishInstallingUpdate:
            break;
          case TapToPayPluginEvent.DidRequestReaderInput:
            break;
          case TapToPayPluginEvent.DidRequestReaderDisplayMessage:
            break;
          case TapToPayPluginEvent.DidCancelDiscoverReaders:
            this.onReaderNotConnected();
            break;
          default:
            log('Unknown event');
            break;
        }
      };

      const handleFailure = (
        reject: (reason?: any) => void,
        errorPayload: CordovaTapToPayErrorPayload = { name: 'unknown', description: '' },
      ) => {
        log(`Connect to reader failed: ${formatError(errorPayload)}`);
        this.onReaderNotConnected();
        reject(errorPayload);
      };

      const connectionStatus = await TapToPayPlugin.connectionStatus();

      if ([
        TapToPayConnectionStatus.Connecting,
        TapToPayConnectionStatus.Connected,
      ].includes(connectionStatus)) {
        throw new StripeTerminalConnectError(`Connection aborted: Reader is ${TapToPayConnectionStatus[connectionStatus]}`);
      }

      try {
        log(`Connecting with locId=${locationId}, name=${merchantName}, OBO=${stripeAcctId}`);
        this.onSoftwareUpdateProgressUpdated(0);
        await new Promise((resolve, reject) => instance.connectToReader(
          event => handleSuccess(resolve, event),
          errorPayload => handleFailure(reject, errorPayload),
        ));
      } catch (e) {
        log('Connection Failed', formatError(e));
        throw new StripeTerminalConnectError(e);
      }
    },
    async disconnectReader({ force = false }: { force: boolean }, state: RootState) {
      if (!state.stripeTerminal.pluginLoaded) {
        log('didAbortReaderDisconnect: Plugin not loaded');
        throw new StripeTerminalDisconnectError('Plugin not loaded');
      }
      try {
        await TapToPayPlugin.disconnectReader();
        log('didReaderDisconnect', `force: ${force}`);
        this.onReaderNotConnected();
      } catch (e) {
        log('didReaderDisconnectFail', `force: ${force}`, e);
        if (e instanceof StripeTerminalDisconnectError) throw e;
        throw new StripeTerminalDisconnectError(e);
      }
    },

    async collectPayment(payload: {
      clientSecret: string,
    }, state: RootState): Promise<void> {
      if (!state.stripeTerminal.pluginLoaded) {
        log('Collect Payment aborted: Plugin not loaded');
        throw new StripeTerminalCollectPaymentError('Plugin not loaded');
      }
      if (state.stripeTerminal.connectionStatus !== ConnectionStatus.Connected) {
        log('Collect Payment aborted: Reader not connected');
        throw new StripeTerminalCollectPaymentError('Reader not connected');
      }

      const handleSuccess = (
        resolve: (value: unknown) => void,
        event: CordovaTapToPayCollectPaymentSuccessPayload,
      ) => {
        if (typeof event !== 'object') return;
        log(JSON.stringify(event));
        switch (event.event) {
          case TapToPayPluginEvent.DidCollectPaymentMethod:
            log('didCollectPaymentMethod');
            break;
          case TapToPayPluginEvent.DidProcessPaymentIntent:
            log('didProcessPaymentIntent');
            resolve(event);
            break;
          default:
            log('Unknown event');
            break;
        }
      };

      const { clientSecret } = payload;
      try {
        log('didStartCollectPayment', clientSecret);
        await new Promise((resolve, reject) => TapToPayPlugin.collectPayment(
          clientSecret,
          event => handleSuccess(resolve, event),
          errorPayload => reject(errorPayload),
        ));
      } catch (e) {
        log('collectPayment Failed', formatError(e));
        if (e?.name === TapToPayPluginErrors.ProcessPaymentError) {
          throw new StripeTerminalProcessPaymentError(e);
        }
        throw new StripeTerminalCollectPaymentError(e);
      }
    },
    async updateGeolocationAuthorizationStatus(payload, state: RootState) {
      if (!state.stripeTerminal.pluginLoaded) {
        log('updateGeolocationAuthorizationStatus aborted: Plugin not loaded');
        throw new StripeTerminalGeolocationAuthorizationError('Plugin not loaded');
      }
      try {
        const status = await TapToPayPlugin.geolocationAuthorizationStatus();
        await this.onGeolocationAuthorizationStatusUpdated(status);
      } catch (e) {
        log('updateGeolocationAuthorizationStatus Failed', e);
        throw new StripeTerminalGeolocationAuthorizationError(e);
      }
    },
  }),
};
